import { useState } from 'react';
import { CreditCard, Plus, Trash2, AlertTriangle } from 'lucide-react';
import { useTheme } from './ThemeContext';
import { useModal } from './AppModal';
import SaveCardModal from './SaveCardModal';

// Helper to format card brand for display
const formatBrand = (brand) => {
  const brands = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'Amex',
    discover: 'Discover',
    diners: 'Diners',
    jcb: 'JCB',
    unionpay: 'UnionPay'
  };
  return brands[brand?.toLowerCase()] || brand || 'Card';
};

// Helper to check if card is expired
const isCardExpired = (expMonth, expYear) => {
  if (!expMonth || !expYear) return false;
  const now = new Date();
  const expDate = new Date(expYear, expMonth); // Month is 0-indexed, so this is end of exp month
  return now > expDate;
};

export default function CustomerCardOnFile({ customer, companyId, onUpdate, stripeConnected = true, isDemo = false }) {
  const { colors } = useTheme();
  const { showConfirm, showError, showSuccess } = useModal();
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [removing, setRemoving] = useState(false);

  const hasCard = customer?.default_payment_method_id && customer?.card_last4;
  const expired = hasCard && isCardExpired(customer.card_exp_month, customer.card_exp_year);

  const handleRemoveCard = async () => {
    const confirmed = await showConfirm(
      'Remove this card from file? This will disable auto-charge for any recurring jobs.',
      { title: 'Remove Card', confirmText: 'Remove', confirmStyle: 'danger' }
    );

    if (!confirmed) return;

    setRemoving(true);
    try {
      const response = await fetch('/api/remove-payment-method', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: customer.id,
          companyId
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to remove card');
      }

      showSuccess('Card removed');
      if (onUpdate) {
        onUpdate({
          ...customer,
          default_payment_method_id: null,
          card_last4: null,
          card_brand: null,
          card_exp_month: null,
          card_exp_year: null,
          card_saved_at: null
        });
      }
    } catch (err) {
      showError(err.message);
    } finally {
      setRemoving(false);
    }
  };

  const handleCardSaved = (card) => {
    if (onUpdate) {
      onUpdate({
        ...customer,
        default_payment_method_id: 'saved', // Actual ID not needed for display
        card_last4: card.last4,
        card_brand: card.brand,
        card_exp_month: card.exp_month,
        card_exp_year: card.exp_year,
        card_saved_at: new Date().toISOString()
      });
    }
  };

  // Don't show if Stripe Connect not set up
  if (!stripeConnected) {
    return null;
  }

  return (
    <>
      <div style={{
        background: colors.bgInput,
        borderRadius: '8px',
        padding: '1rem',
        border: `1px solid ${expired ? colors.warning : colors.border}`
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: hasCard ? '0.75rem' : 0
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: colors.textMuted,
            fontSize: '0.85rem',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            <CreditCard size={16} />
            Card on File
          </div>

          {hasCard ? (
            <button
              onClick={handleRemoveCard}
              disabled={removing}
              style={{
                background: 'none',
                border: 'none',
                color: colors.error,
                cursor: removing ? 'not-allowed' : 'pointer',
                padding: '0.25rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                fontSize: '0.8rem',
                opacity: removing ? 0.5 : 1
              }}
            >
              <Trash2 size={14} />
              {removing ? 'Removing...' : 'Remove'}
            </button>
          ) : (
            <button
              onClick={() => setShowSaveModal(true)}
              style={{
                background: colors.accent,
                color: '#fff',
                border: 'none',
                padding: '0.4rem 0.75rem',
                borderRadius: '6px',
                fontSize: '0.8rem',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.35rem'
              }}
            >
              <Plus size={14} />
              Add Card
            </button>
          )}
        </div>

        {hasCard && (
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              {/* Card icon/brand */}
              <div style={{
                background: colors.bg,
                padding: '0.5rem 0.75rem',
                borderRadius: '6px',
                fontWeight: 'bold',
                color: colors.text,
                fontSize: '0.9rem'
              }}>
                {formatBrand(customer.card_brand)}
              </div>

              {/* Card number */}
              <div style={{
                color: colors.text,
                fontSize: '1rem',
                fontFamily: 'monospace',
                letterSpacing: '1px'
              }}>
                •••• {customer.card_last4}
              </div>

              {/* Expiry */}
              <div style={{
                color: expired ? colors.warning : colors.textMuted,
                fontSize: '0.85rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.35rem'
              }}>
                {expired && <AlertTriangle size={14} />}
                {String(customer.card_exp_month).padStart(2, '0')}/{String(customer.card_exp_year).slice(-2)}
              </div>
            </div>

            {expired && (
              <div style={{
                marginTop: '0.75rem',
                padding: '0.5rem 0.75rem',
                background: `${colors.warning}20`,
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <span style={{ color: colors.warning, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <AlertTriangle size={14} />
                  Card expired
                </span>
                <button
                  onClick={() => setShowSaveModal(true)}
                  style={{
                    background: colors.warning,
                    color: '#000',
                    border: 'none',
                    padding: '0.35rem 0.65rem',
                    borderRadius: '4px',
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Update
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {showSaveModal && (
        <SaveCardModal
          customer={customer}
          companyId={companyId}
          onClose={() => setShowSaveModal(false)}
          onSaved={handleCardSaved}
          isDemo={isDemo}
        />
      )}
    </>
  );
}
